import { useMemo } from "react";
import { StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import { DEFAULT_WEEK_START_DAY, weekStartNAgo, type Point } from "@heraion/shared";

import { BarChart } from "../charts/BarChart";
import { useChartReadout } from "../charts/pointSelection";
import type { UiTheme } from "../theme";
import { makeTierOneStyles } from "./panelStyles";

// PG-64 fetta 2: one week per bar, for Rhythm, Streak and volume by tag. The
// PG-63 row model (TierOneTrendRow's bars): a fixed header line names the
// series and reads the selected week, «value · week», the latest by default;
// a tap on a bar changes it, and the value is not repeated beside the name.
// Every bar prints its number and its week under it. The current week is the
// in-progress outline, its number printed like the others: it already counts.

export type WeekBarsCardProps = {
  theme: UiTheme;
  /** The series' name, left of the readout. */
  title: string;
  /** One value per calendar week, oldest first, the current week last. */
  values: ReadonlyArray<number>;
  nowMs: number;
  /** The athlete's week start (Profile.weekStartDay). */
  weekStartDay?: number;
  locale: string;
  /** The number printed on a bar. */
  valueFormat: (y: number) => string;
  /** The value in the header and in each bar's spoken name. Defaults to
   *  `valueFormat`. */
  readoutFormat?: (y: number) => string;
  /** The last week is still running. */
  partialLast?: boolean;
};

export function WeekBarsCard({
  theme,
  title,
  values,
  nowMs,
  weekStartDay = DEFAULT_WEEK_START_DAY,
  locale,
  valueFormat,
  readoutFormat,
  partialLast = false,
}: WeekBarsCardProps) {
  const { t } = useTranslation();
  const styles = useMemo(() => makeTierOneStyles(theme), [theme]);
  const points = useMemo<Point[]>(() => {
    const day = new Intl.DateTimeFormat(locale, { day: "numeric", month: "short" });
    const last = values.length - 1;
    return values.map((y, i) => {
      const x = weekStartNAgo(nowMs, weekStartDay, last - i);
      const label =
        partialLast && i === last
          ? t("mobile.progress.weekBars.thisWeek", { defaultValue: "this week so far" })
          : t("mobile.progress.weekBars.weekOf", {
              date: day.format(new Date(x)),
              defaultValue: "week of {{date}}",
            });
      return { x, y, label };
    });
  }, [values, nowMs, weekStartDay, locale, partialLast, t]);
  const tick = useMemo(() => {
    const short = new Intl.DateTimeFormat(locale, { day: "numeric", month: "numeric" });
    return (p: Point) => short.format(new Date(p.x));
  }, [locale]);
  const { selection, readout, readoutProps } = useChartReadout(
    points,
    readoutFormat ?? valueFormat,
    false,
  );
  if (readout === undefined) return null;
  return (
    <View style={styles.trendRow}>
      {/* The name is never cut: when name and readout do not fit one line,
          the readout wraps under it. */}
      <View style={[styles.trendLabelRow, head.row]}>
        <Text style={[styles.balanceLabel, head.title]}>{title}</Text>
        <Text
          style={[styles.trendFlag, head.readout, { color: theme.palette.ink }]}
          {...readoutProps}
        >
          {readout}
        </Text>
      </View>
      <BarChart
        theme={theme}
        points={points}
        valueFormat={valueFormat}
        readoutFormat={readoutFormat}
        selection={selection}
        tick={tick}
        partialLast={partialLast}
      />
    </View>
  );
}

const head = StyleSheet.create({
  row: { flexWrap: "wrap" },
  // balanceLabel is `flex: 1` (it truncates); here the name keeps its width.
  title: { flexGrow: 1, flexShrink: 0, flexBasis: "auto" },
  readout: { flexShrink: 1 },
});
